import { get, post, put, del, upload } from '../http'
import axios from 'axios'
import { applyAiUploadLidToFormData } from '@/utils/script-recording'
import { putBlobToPresignedUrl } from '@/utils/ossAudioUpload'
const { API_URL, AUDIO_UPLOAD_USE_OSS } = require('../../../config')

/**
 * 是否启用 OSS（预签名直传/OSS 存储）。本地开发环境为 false，
 * 波形下载/整段音频请求应回退到平台流（/api/audio/stream/:id），不要请求生产 OSS 签名 URL。
 */
export const OSS_AUDIO_ENABLED = !!AUDIO_UPLOAD_USE_OSS

/** 预签名 / complete 与直传大文件 */
const AUDIO_OSS_UPLOAD_TIMEOUT_MS = 300000
const AUDIO_OSS_COMPLETE_RETRY_COUNT = 20
const AUDIO_OSS_COMPLETE_RETRY_INTERVAL_MS = 3000

/** 与后端 error: file_format_invalid / MP3 文件头校验失败 对应的界面提示 */
const MSG_AUDIO_FORMAT_INVALID = '文件不是有效的MP3/WAV格式，请调整格式后上传'

function sleep (ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * 从 axios 错误或接口 body 中识别「音频格式无效」类错误，返回统一提示文案
 * @param {*} errOrBody axios 错误对象，或含 response.data / 直接为接口 body
 * @returns {string|null}
 */
function pickAudioFormatInvalidMessage (errOrBody) {
  const data = errOrBody?.response?.data ?? errOrBody
  if (!data || typeof data !== 'object') return null
  if (data.error === 'file_format_invalid') return MSG_AUDIO_FORMAT_INVALID
  const m = data.message
  if (typeof m === 'string' && (m.includes('文件头验证') || m.includes('不是有效的MP3'))) {
    return MSG_AUDIO_FORMAT_INVALID
  }
  return null
}

function isApiOk (res) {
  if (!res || typeof res !== 'object') return false
  if (res.success === true) return true
  const c = res.code
  return c === 200 || c === 0
}

function isMountNotReadyResponse (errOrBody) {
  const data = errOrBody?.response?.data ?? errOrBody
  return data && typeof data === 'object' && data.error === 'mount_not_ready'
}

function pickPresignPayload (res) {
  if (!res || typeof res !== 'object') return null
  const d = res.data
  if (d && typeof d === 'object' && (d.uploadUrl || d.presignId != null)) return d
  if (res.uploadUrl || res.presignId != null) return res
  return null
}

function getAudioFileFromFormData (formData) {
  const f = formData.get('files')
  if (f instanceof File || f instanceof Blob) return f
  const f2 = formData.get('file')
  if (f2 instanceof File || f2 instanceof Blob) return f2
  return null
}

function formDataToOssPresignPayload (formData, file) {
  const payload = {
    workId: formData.get('workId'),
    type: String(formData.get('type') ?? ''),
    originalFilename: file.name,
    contentType: (file.type && String(file.type).trim()) || 'application/octet-stream',
    fileSize: file.size
  }
  if (payload.workId != null && payload.workId !== '') {
    const n = Number(payload.workId)
    if (Number.isFinite(n)) payload.workId = n
  }
  const optionalKeys = ['description', 'trackNumber', 'lid', 'ttsAi', 'audioSource']
  for (const k of optionalKeys) {
    if (!formData.has(k)) continue
    const v = formData.get(k)
    if (v == null || v === '') continue
    payload[k] = typeof v === 'string' ? v : String(v)
  }
  return payload
}

/**
 * 获取作品列表
 * @returns {Promise<Object>}
 */
export function getWorks(params = {}) {
  return get('/api/audio/works', params)
}

/**
 * 获取音频列表
 * @param {Object} params 查询参数
 * @param {number} [params.canSettle] 作品所属制作组可结算标记（0/1），与 GET /api/audio/works 一致；不传则不过滤
 * @returns {Promise<Object>}
 */
export function getAudioList(params) {
  return get('/api/audio/list', params)
}

/**
 * AI 自然语言搜索音频
 * @param {string} query 自然语言搜索内容
 * @param {Object} [params] 分页参数（page / pageSize）
 * @returns {Promise<Object>}
 */
export function aiSearchAudio(query, params = {}) {
  return post('/api/audio/ai-search', { query, ...params })
}

/**
 * 获取音频详情
 * @param {number|string} id 音频ID
 * @returns {Promise<Object>}
 */
export function getAudioDetail(id) {
  return get(`/api/audio/${id}`)
}

/**
 * 开发环境：multipart 直传后端 /api/audio/upload（不经 OSS）
 * @param {{ onPutProgress?: (e: { loaded: number, total: number, lengthComputable: boolean }) => void }} [options]
 */
async function uploadAudioMultipartDirect (formData, options = {}) {
  const { onPutProgress } = options
  const config = {
    timeout: AUDIO_OSS_UPLOAD_TIMEOUT_MS
  }
  if (typeof onPutProgress === 'function') {
    config.onUploadProgress = (pe) => {
      if (pe.total) {
        onPutProgress({
          loaded: pe.loaded,
          total: pe.total,
          lengthComputable: true
        })
      }
    }
  }
  let res
  try {
    res = await post('/api/audio/upload', formData, config)
  } catch (e) {
    const friendly = pickAudioFormatInvalidMessage(e)
    if (friendly) return Promise.reject(new Error(friendly))
    const serverMsg = e?.response?.data?.message
    return Promise.reject(new Error(serverMsg || e?.message || '上传失败'))
  }
  if (!isApiOk(res)) {
    const friendly = pickAudioFormatInvalidMessage(res)
    if (friendly) return Promise.reject(new Error(friendly))
    return Promise.reject(new Error(res?.message || '上传失败'))
  }
  return res
}

/**
 * 上传音频文件
 * - 线上 API（非本机）：OSS 预签名 PUT → upload-oss-complete
 * - 本地开发 API：POST /api/audio/upload multipart，不访问阿里云 OSS
 * @param {FormData} formData 与原先一致：workId、type、files 或 file，可选 description、trackNumber、lid 等
 * @param {{ onPutProgress?: (e: ProgressEvent | { loaded: number, total: number, lengthComputable: boolean }) => void }} [options] 上传进度
 * @returns {Promise<Object>} 接口响应体（含 code、data 等）
 */
export async function uploadAudio (formData, options = {}) {
  const file = getAudioFileFromFormData(formData)
  if (!file || !(file instanceof Blob)) {
    return Promise.reject(new Error('缺少音频文件（FormData 中需含 files 或 file）'))
  }
  try {
    applyAiUploadLidToFormData(formData)
  } catch (e) {
    if (e?.code === 'lid_required_for_ai_upload') {
      return Promise.reject(e)
    }
    throw e
  }
  if (!AUDIO_UPLOAD_USE_OSS) {
    return uploadAudioMultipartDirect(formData, options)
  }
  const payload = formDataToOssPresignPayload(formData, file)
  const { onPutProgress } = options
  const presignRes = await post('/api/audio/upload-oss-presign', payload, {
    timeout: AUDIO_OSS_UPLOAD_TIMEOUT_MS
  })
  if (!isApiOk(presignRes)) {
    const msg = presignRes?.message || '预签名失败'
    return Promise.reject(new Error(msg))
  }
  const presign = pickPresignPayload(presignRes)
  if (!presign?.uploadUrl || presign.presignId == null) {
    return Promise.reject(new Error(presignRes?.message || '预签名响应无效'))
  }
  try {
    await putBlobToPresignedUrl(presign.uploadUrl, file, {
      headers: presign.headers,
      onProgress: onPutProgress
    })
  } catch (e) {
    return Promise.reject(e)
  }
  const completeBody = { presignId: presign.presignId }
  if (formData.has('originalname')) {
    const on = formData.get('originalname')
    if (on != null && String(on).trim() !== '') completeBody.originalname = String(on)
  }
  let completeRes
  for (let attempt = 0; attempt <= AUDIO_OSS_COMPLETE_RETRY_COUNT; attempt++) {
    try {
      completeRes = await post('/api/audio/upload-oss-complete', completeBody, {
        timeout: AUDIO_OSS_UPLOAD_TIMEOUT_MS
      })
      if (isMountNotReadyResponse(completeRes) && attempt < AUDIO_OSS_COMPLETE_RETRY_COUNT) {
        await sleep(AUDIO_OSS_COMPLETE_RETRY_INTERVAL_MS)
        continue
      }
      break
    } catch (e) {
      if (isMountNotReadyResponse(e) && attempt < AUDIO_OSS_COMPLETE_RETRY_COUNT) {
        await sleep(AUDIO_OSS_COMPLETE_RETRY_INTERVAL_MS)
        continue
      }
      const friendly = pickAudioFormatInvalidMessage(e)
      if (friendly) return Promise.reject(new Error(friendly))
      const serverMsg = e?.response?.data?.message
      return Promise.reject(new Error(serverMsg || e?.message || '完成上传失败'))
    }
  }
  if (!isApiOk(completeRes)) {
    const friendly = pickAudioFormatInvalidMessage(completeRes)
    if (friendly) return Promise.reject(new Error(friendly))
    return Promise.reject(new Error(completeRes?.message || '完成上传失败'))
  }
  return completeRes
}

/**
 * 批量上传音频文件
 * @param {Array<File>} files 音频文件数组
 * @param {Object} data 附加数据，如workId, description等
 * @returns {Promise<Array>}
 */
export async function batchUploadAudio (files, data = {}) {
  const promises = files.map((file) => {
    const fd = new FormData()
    Object.entries(data || {}).forEach(([k, v]) => {
      if (v == null || k === 'files') return
      fd.append(k, typeof v === 'string' || v instanceof Blob ? v : String(v))
    })
    const blob = file?.raw instanceof Blob ? file.raw : file
    if (!(blob instanceof Blob)) {
      return Promise.reject(new Error('无效的文件项'))
    }
    fd.append('files', blob, blob.name || 'audio')
    return uploadAudio(fd)
  })
  return Promise.all(promises)
}

/**
 * 更新音频信息
 * @param {number|string} id 音频ID
 * @param {Object} data 更新数据
 * @returns {Promise<Object>}
 */
export function updateAudio(id, data) {
  // 确保传递正确的workId
  const updateData = { ...data }
  if (data.workId) {
    updateData.teamId = data.workId // 后端API使用teamId字段
  }
  return put(`/api/audio/${id}`, updateData)
}

/**
 * 删除音频
 * @param {number|string} id 音频ID
 * @returns {Promise<Object>}
 */
export function deleteAudio(id) {
  return del(`/api/audio/delete/${id}`)
}

/**
 * 按轨道删除音频
 * @param {number} workId 作品ID
 * @param {string} trackNumber 轨道号（格式：XX-Y，例如 "1-1", "5-3"）
 * @param {string} archiveType 归档类型（可选，如果不提供则删除所有类型的音频）
 * @returns {Promise<Object>}
 */
export function deleteAudioByTrack(workId, trackNumber, archiveType) {
  const data = {
    workId,
    trackNumber
  }
  if (archiveType) {
    data.archiveType = archiveType
  }
  return post('/api/audio/delete-by-track', data)
}

/**
 * 批量删除音频
 * @param {Array<number|string>} filenames 音频文件名数组
 * @returns {Promise<Array>}
 */
export async function batchDeleteAudio(filenames) {
  const promises = filenames.map(filename => deleteAudio(filename))
  return Promise.all(promises)
}

/**
 * 下载音频（支持 302 重定向）
 * @param {number|string} id 音频ID
 * @param {Function} onProgress 下载进度回调函数 (progressEvent) => {}
 * @returns {Promise<Blob>}
 */
export async function downloadAudio(id, onProgress) {
  const token = localStorage.getItem('token')
  
  try {
    // 音频下载接口返回 302 重定向，使用 fetch 来处理重定向和进度
    const url = `${API_URL}/api/audio/download/${id}`
    const headers = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    
    // 使用 fetch API 来处理重定向和进度
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      redirect: 'follow' // 自动跟随重定向
    })
    
    if (!response.ok) {
      throw new Error(`下载失败: ${response.status} ${response.statusText}`)
    }
    
    // 获取 Content-Length 用于计算进度
    const contentLength = response.headers.get('content-length')
    const total = contentLength ? parseInt(contentLength, 10) : 0
    
    // 读取响应流并跟踪进度
    const reader = response.body.getReader()
    const chunks = []
    let loaded = 0
    
    while (true) {
      const { done, value } = await reader.read()
      
      if (done) {
        break
      }
      
      chunks.push(value)
      loaded += value.length
      
      // 调用进度回调
      if (onProgress && total > 0) {
        onProgress({
          loaded: loaded,
          total: total,
          lengthComputable: true
        })
      }
    }
    
    // 合并所有 chunks 为 Blob
    const blob = new Blob(chunks)
    return blob
  } catch (error) {
    if (error.message) {
      throw error
    } else {
      throw new Error(error.message || '下载失败，请重试')
    }
  }
}

/**
 * 批量下载音频
 * @param {Array<number|string>} filenames 音频文件名数组
 * @returns {Promise<Array<Blob>>}
 */
export async function batchDownloadAudio(filenames) {
  const promises = filenames.map(filename => downloadAudio(filename))
  return Promise.all(promises)
}

/**
 * 获取音频流地址
 * @param {string|number} id 音频ID
 * @returns {string} 流媒体地址
 */
export function getAudioStreamUrl(id) {
  if (!id && id !== 0) {
    console.warn('getAudioStreamUrl: id is invalid', id)
    return ''
  }
  const baseUrl = `${API_URL}/api/audio/stream/${id}`
  try {
    const token = localStorage.getItem('token')
    return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl
  } catch (error) {
    return baseUrl
  }
}

/**
 * 获取不带 token 的音频流地址，用于持久化存储
 * @param {string|number} id 音频ID
 * @returns {string}
 */
export function getAudioStreamBaseUrl(id) {
  if (!id && id !== 0) {
    console.warn('getAudioStreamBaseUrl: id is invalid', id)
    return ''
  }
  return `${API_URL}/api/audio/stream/${id}`
}

/**
 * 获取受权限保护的 OSS 临时下载地址。
 * 仅用于波形分析等需要整段下载的场景，实际播放仍使用 getAudioStreamUrl。
 * @param {string|number} id 音频ID
 * @returns {Promise<Object>}
 */
export function getAudioOssDownloadUrl(id) {
  if (!id && id !== 0) {
    return Promise.reject(new Error('音频ID无效'))
  }
  return get(`/api/audio/oss-download-url/${id}`)
}

/**
 * 获取音频WEB API流地址（用于WEB端在线播放）
 * 与 getAudioStreamUrl 一致，后端路由为 /api/audio/stream/:id
 * @param {string|number} id 音频ID
 * @returns {string} 流媒体地址
 */
export function getAudioWebApiStreamUrl(id) {
  if (!id && id !== 0) {
    console.warn('getAudioWebApiStreamUrl: id is invalid', id)
    return ''
  }
  return getAudioStreamUrl(id)
}

/**
 * 获取当前用户最近上传的音频列表
 * @returns {Promise} 返回用户最近上传的音频列表
 */
export function getUserRecentUploads() {
  return get('/api/audio/user/recent-uploads')
}

/**
 * 校验音频文件MD5
 * @param {Object} data { md5: string, filename: string }
 * @returns {Promise<Object>}
 */
export function checkAudioMd5s(data) {
  return post('/api/audio/check-md5s', data)
}

/**
 * 重新计算团队所有音频的有效时长
 * @param {string|number} teamId 团队ID
 * @returns {Promise<Object>} 返回推送的音频数量等信息
 */
export function recalcAudioDurationAct(teamId) {
  return post('/api/audio/recalc-duration-act', { teamId })
}

/**
 * 根据集数和作品ID获取音频列表（用于在线审片）
 * @param {number} workId 作品ID
 * @param {number} episodeNumber 集数
 * @returns {Promise<Object>}
 */
export function getAudioByEpisode(workId, episodeNumber) {
  return get('/api/audio/by-episode', {
    workId,
    episodeNumber
  })
}

/**
 * 替换音频文件
 * @param {number} audioId 要替换的音频ID
 * @param {number} workId 作品ID
 * @param {File} file 新的音频文件
 * @param {string} [trackNumber] 轨道号
 * @param {Array<number|string>} [audioIdsToDelete] 可选，随表单以 JSON 字符串提交
 * @returns {Promise<Object>}
 */
export function replaceAudio(audioId, workId, file, trackNumber, audioIdsToDelete) {
  const formData = new FormData()
  formData.append('audioId', String(audioId))
  formData.append('workId', String(workId))
  // 如果提供了轨道号，添加到formData中
  if (trackNumber !== undefined && trackNumber !== null) {
    formData.append('trackNumber', String(trackNumber))
  }
  if (Array.isArray(audioIdsToDelete) && audioIdsToDelete.length) {
    formData.append('audioIdsToDelete', JSON.stringify(audioIdsToDelete))
  }
  // 直接使用File对象，浏览器会自动处理UTF-8编码的文件名
  // FormData会自动处理文件名的编码，确保中文文件名正确传递
  formData.append('file', file)

  return post('/api/audio/replace', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 修改音频名称
 * @param {number|string} id 音频ID
 * @param {string} filename 新文件名（不含扩展名）
 * @returns {Promise<Object>}
 */
export function updateAudioName(id, filename) {
  return put(`/api/audio/${id}/name`, { filename })
}

/**
 * 在线审片逻辑删除音频：服务端只标记 isdeleted=1，不删除物理文件
 * @param {number|string} id 音频ID
 * @returns {Promise<Object>}
 */
export function reviewLogicalDeleteAudio(id) {
  return post(`/api/audio/${id}/review-logical-delete`)
}

export default {
  getWorks,
  getAudioList,
  getAudioDetail,
  uploadAudio,
  batchUploadAudio,
  updateAudio,
  deleteAudio,
  batchDeleteAudio,
  downloadAudio,
  batchDownloadAudio,
  getAudioStreamUrl,
  getAudioOssDownloadUrl,
  getUserRecentUploads,
  checkAudioMd5s,
  recalcAudioDurationAct,
  getAudioByEpisode,
  replaceAudio,
  deleteAudioByTrack,
  updateAudioName,
  reviewLogicalDeleteAudio
} 
